import { useState, useEffect, useRef } from 'react';
import type { RouteConfig } from '@/types/route';
import { useLocale } from '@/hooks/useLocale';
import { useStore } from '../../../store'
import { useToast } from '@/components/Toast';
import type { Dayjs } from 'dayjs';
import { Table, Drawer, Input } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import GenericQueryCard, { QueryField } from '@/components/GenericQueryCard';
import { Pagination } from "@/components/Pagination";
import type { ClientGroup, ClientGroupDetails } from '@/types/customerManagement';
import { fetchAgentID, fetchClientGroupDetails, fetchClientGroupList, saveClientGroup, upsertClientGroupMember } from '@/services/customerManagement';
import { MobileTableCard } from '@/components/MobileCards';
import dayjs from 'dayjs';

export const routeConfig: RouteConfig = {
    title: 'route.customerManagement.clientGrouping',
    layout: true,
    sort: 1
};

// interface ClientGroup {
//     groupId: string,
//     groupName: string,
//     creationTime: number, // unix timestamp
//     memberCount: number,
//     depositorCount: number,
//     totalDeposit: number,
//     totalVolume: number,
//     totalCommission: number,
//     operation?: React.ReactNode;
// }

// interface ClientGroupDetails {
//     groupId?: string; // Group ID auto generate by backend
//     groupName: string;
//     spotRatio?: number;
//     spotMyRatio?: number;
//     futuresRatio?: number;
//     futuresMyRatio?: number;
//     alphaRatio?: number;
//     alphaMyRatio?: number;
//     members?: number[];
//     creationTime?: number; // creation time in unix timestamp auto generate current time
// }

const ClientGroup = () => {
    const { t } = useLocale();
    const { UserStore } = useStore();
    const toast = useToast();

    const [groupName, setGroupName] = useState('');
    const [selectedCustomerLabel, setSelectedCustomerLabel] = useState(0);
    const [creationTime, setCreationTime] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
    const [queryTime, setQueryTime] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
    const [loading, setLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(0);
    const [clientGroupData, setClientGroupData] = useState<ClientGroup[]>([]);
    const [clientGroupDetailsData, setClientGroupDetailsData] = useState<ClientGroupDetails[]>([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [total, setTotal] = useState<number>(0);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [isCreate, setIsCreate] = useState(false);

    // For managing client drawer
    const [selectedRecord, setSelectedRecord] = useState<ClientGroupDetails | null>(null);
    const [maxSpotRatio, setMaxSpotRatio] = useState<number>(0);
    const [maxFuturesRatio, setMaxFuturesRatio] = useState<number>(0);

    // --- User selection logic for group members ---
    const [allUsers, setAllUsers] = useState<{ uid: string }[]>([]);
    const [searchValue, setSearchValue] = useState('');
    const [selectedUIDs, setSelectedUIDs] = useState<string[]>([]);

    // Only show users not already selected
    const filteredUsers = allUsers.filter(user => !selectedUIDs.includes(user.uid));

    // Helper to build params for client group API
    const buildClientGroupParams = (groupName: string, creationTime?: [Dayjs | null, Dayjs | null]) => {
        let params: any = {};
        if (groupName !== '') {
            params.group_name = groupName;
        }
        // if (selectedCustomerGroup !== 0) {
        //     params.group_name = selectedCustomerGroup;
        // }
        // if (transactionTime[0] && transactionTime[1]) {
        //     params.time_range = [transactionTime[0].unix(), transactionTime[1].unix()];
        // }
        return params;
    };

    const handleReset = () => {
        setGroupName('');
        setSelectedCustomerLabel(0);
        setCreationTime([null, null]);
        setQueryTime([null, null]);
        setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page
        fetchClientGroupList({}, 1, pagination.pageSize).then(res => {
            setClientGroupData(res.list);
            setTotal(res.total);
        });
    };

    const handleQuery = () => {
        // Convert to unix seconds if both dates are present
        // let creationTimeUnix: [number|null, number|null] = [null, null];
        // if (creationTime[0] && creationTime[1]) {
        //     creationTimeUnix = [creationTime[0].unix(), creationTime[1].unix()];
        // }

        // let queryTimeUnix: [number|null, number|null] = [null, null];
        // if (queryTime[0] && queryTime[1]) {
        //     queryTimeUnix = [queryTime[0].unix(), queryTime[1].unix()];
        // }

        const params = buildClientGroupParams(groupName, creationTime);
        setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page
        fetchClientGroupList(params, 1, pagination.pageSize).then(res => {
            setClientGroupData(res.list);
            setTotal(res.total);
        });
    };

    const handleCreateClick = async () => {
        setSelectedRecord({
            group: {
                groupId: '',
                groupName: '',
                creationTime: '',
                memberCount: 0,
                depositorCount: 0,
                totalDeposit: 0,
                totalVolume: 0,
                totalCommission: 0,
                spotRatio: 0,
                futuresRatio: 0,
                alphaRatio: 0,
            },
            members: []
        });
        setSelectedUIDs([]);
        setIsCreate(true);

        const agentIDs = await fetchAgentID('', 1);
        setAllUsers(agentIDs.map((uid: string) => ({ uid }))); // Fetch all users for selection
        setDrawerOpen(true);
    };

    const handleManageClick = async (record: ClientGroup) => {
        // Find the corresponding details
        try {
            const details = await fetchClientGroupDetails({ group_id: record.groupId });

            if (details) {
                // Clone to avoid direct mutation
                setSelectedRecord({ ...details });
                setSelectedUIDs(details.members.map(member => member.userId));

                const agentIDs = await fetchAgentID('', 1);
                setAllUsers(agentIDs.map((uid: string) => ({ uid }))); // Fetch all users for selection
                setIsCreate(false);
                setDrawerOpen(true);
            }
        } catch (error) {
            console.error('Error managing client group:', error);
        }
    };

    const handleSelectUser = (uid: string) => {
        setSelectedUIDs(prev => prev.includes(uid) ? prev : [...prev, uid]);
    };

    const handleDrawerClose = () => {
        setDrawerOpen(false);
        setIsCreate(false);
        setSelectedRecord(null);
        setSelectedUIDs([]);
    };

    const handleDrawerSave = async () => {
        if (!selectedRecord) return;

        let groupId = selectedRecord.group.groupId;
        let isCreateMode = !groupId;

        // For modify, check ratio increase only
        if (!isCreateMode) {
            const original = clientGroupData.find(item => item.groupId === groupId);
            if (original) {
                if (
                    (selectedRecord.group.spotRatio ?? 0) < (original.spotRatio ?? 0) ||
                    (selectedRecord.group.futuresRatio ?? 0) < (original.futuresRatio ?? 0)
                ) {
                    toast.error({
                        message: t('customerManagement.clientGroup.modifyRatioIncreaseOnly') || 'You can only increase the ratios.'
                    });
                    return;
                }
            }
        }

        // Save group (create or modify)
        let saveResult;
        if (isCreateMode) {
            saveResult = await saveClientGroup({
                user_id: UserStore.userId || '',
                group_name: selectedRecord?.group.groupName || '',
                description: selectedRecord?.group.description || '',
                contract_rebate_rate: selectedRecord?.group.futuresRatio || 0,
                spot_rebate_rate: selectedRecord?.group.spotRatio || 0
            });
            // If backend returns new groupId, use it
            groupId = saveResult?.groupId || groupId;
        } else {
            await saveClientGroup({
                id: groupId || '',
                user_id: UserStore.userId || '',
                group_name: selectedRecord?.group.groupName || '',
                description: selectedRecord?.group.description || '',
                contract_rebate_rate: selectedRecord?.group.futuresRatio || 0,
                spot_rebate_rate: selectedRecord?.group.spotRatio || 0
            });
        }

        // Upsert members if there are new UIDs and groupId is available
        const originalUIDs = (selectedRecord.members || []).map(m => m.userId);
        const hasNewUID = selectedUIDs.some(uid => !originalUIDs.includes(uid));
        if (hasNewUID && groupId) {
            await upsertClientGroupMember({
                group_id: groupId,
                user_ids: selectedUIDs,
                type: 1
            });
        }

        toast.success({
            message: isCreateMode
                ? t('customerManagement.clientGroup.createSuccess') || 'Created successfully.'
                : t('customerManagement.clientGroup.modifySuccess') || 'Saved successfully.'
        });

        // Refresh list
        const params = buildClientGroupParams(groupName, creationTime);
        try {
            const res = await fetchClientGroupList(params, 1, pagination.pageSize);
            setClientGroupData(res.list);
            setTotal(res.total);
        } catch (fetchError: any) {
            toast.error({
                message: fetchError?.message || t('customerManagement.clientGroup.fetchError') || 'An error occurred while refreshing client list.'
            });
        }

        setDrawerOpen(false);
        setIsCreate(false);
        setSelectedRecord(null);
    };

    const formatDate = (dateString?: string, time?: boolean) => {
        if (dateString === undefined || dateString === null || dateString === '') return t('customerManagement.clientGroup.none');
        if (dateString === '0') return '-';
        let date: dayjs.Dayjs;
        if (/^\d{10}$/.test(dateString)) {
            date = dayjs(Number(dateString) * 1000);
        } else {
            date = dayjs(dateString);
        }
        return time ? date.format('YYYY-MM-DD HH:mm:ss') : date.format('YYYY-MM-DD');
    };

    const formatNumber = (num: number | string | null | undefined) => {
        if (num === null || num === undefined) return '--';
        if (isNaN(Number(num))) return '--';
        // 1000000.023024 = 1,000,000.02
        return Number(num).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 4 });
    };

    const queryFields: QueryField[] = [
        {
            type: 'input',
            name: 'groupName',
            label: t('customerManagement.clientGroup.groupName'),
            value: groupName,
            onChange: setGroupName,
            placeholder: t('customerManagement.clientGroup.pleaseEnterGroupNamePlaceholder'),
        },
        // {
        //     type: 'dateRange',
        //     name: 'creationTime',
        //     label: t('customerManagement.clientGroup.creationTime'),
        //     value: creationTime,
        //     onChange: (dates: [Dayjs | null, Dayjs | null] | null) => {
        //         setCreationTime(dates ?? [null, null]);
        //     },
        //     placeholder: [t('customerManagement.clientGroup.startTime'), t('customerManagement.clientGroup.endTime')],
        // },
        // {
        //     type: 'dateRange',
        //     name: 'queryTime',
        //     label: t('customerManagement.clientGroup.queryTime'),
        //     value: queryTime,
        //     onChange: (dates: [Dayjs | null, Dayjs | null] | null) => {
        //         setQueryTime(dates ?? [null, null]);
        //     },
        //     placeholder: [t('customerManagement.clientGroup.startTime'), t('customerManagement.clientGroup.endTime')],
        // }
    ];

    const clientGroupColumns: ColumnsType<ClientGroup> = [
        {
            title: t('customerManagement.clientGroup.table.groupId'),
            dataIndex: 'groupId',
            key: 'groupId',
            align: 'start',
            width: 100,
            ellipsis: true,
        },
        {
            title: t('customerManagement.clientGroup.table.groupName'),
            dataIndex: 'groupName',
            key: 'groupName',
            align: 'start',
            width: 100,
            ellipsis: true,
        },
        {
            title: t('customerManagement.clientGroup.table.creationTime'),
            dataIndex: 'creationTime',
            key: 'creationTime',
            align: 'start',
            width: 150,
            ellipsis: true,
            render: (text) => formatDate(text),
        },
        {
            title: t('customerManagement.clientGroup.table.memberCount'),
            dataIndex: 'memberCount',
            key: 'memberCount',
            align: 'start',
            width: 90,
            ellipsis: true
        },
        {
            title: t('customerManagement.clientGroup.table.spotRatio'),
            dataIndex: 'spotRatio',
            key: 'spotRatio',
            align: 'start',
            width: 90,
            ellipsis: true,
            render: (text) => text + '%'
        },
        {
            title: t('customerManagement.clientGroup.table.futuresRatio'),
            dataIndex: 'futuresRatio',
            key: 'futuresRatio',
            align: 'start',
            width: 90,
            ellipsis: true,
            render: (text) => text + '%'
        },
        // {
        //     title: t('customerManagement.clientGroup.table.depositorCount'),
        //     dataIndex: 'depositorCount',
        //     key: 'depositorCount',
        //     align: 'start',
        //     width: 90,
        //     ellipsis: true
        // },
        // {
        //     title: t('customerManagement.clientGroup.table.totalDeposit'),
        //     dataIndex: 'totalDeposit',
        //     key: 'totalDeposit',
        //     align: 'start',
        //     width: 140,
        //     ellipsis: true
        // },
        // {
        //     title: t('customerManagement.clientGroup.table.totalVolume'),
        //     dataIndex: 'totalVolume',
        //     key: 'totalVolume',
        //     align: 'start',
        //     width: 170,
        //     ellipsis: true
        // },
        // {
        //     title: t('customerManagement.clientGroup.table.totalCommission'),
        //     dataIndex: 'totalCommission',
        //     key: 'totalCommission',
        //     align: 'start',
        //     width: 170,
        //     ellipsis: true,
        // },
        {
            title: t('customerManagement.clientGroup.table.operation'),
            dataIndex: 'operation',
            key: 'operation',
            align: 'end',
            width: 110,
            fixed: 'right',
            render: (_, record) => (
                <div className="grid grid-cols-3 md:flex items-center justify-start md:justify-end gap-2">
                    <button 
                        className="text-sm font-normal text-special-text font-inter w-14 bg-transparent border-none flex justify-center items-center"
                        onClick={() => handleManageClick(record)}
                    >
                        {t('customerManagement.clientGroup.table.manage')}
                    </button>
                </div>
            )
        }
    ];

    // Prepare columns for mobile card (operation column marked)
    const mobileClientGroupColumns = clientGroupColumns
        .filter((col: any) => 'dataIndex' in col)
        .map((col: any) =>
            col.dataIndex === 'operation' ? { ...col, isOperation: true } : col
        );

    // Sample data for demonstration
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                setMaxSpotRatio(UserStore.userSpotRatio); // Example max spot ratio
                setMaxFuturesRatio(UserStore.userRewardRatio); // Example max futures ratio

                // try {
                //     const groupRes = await fetchGroupID({});
                //     setCustomerGroups(groupRes);
                // } catch (groupError: any) {
                //     toast.error({
                //         message: groupError?.message || t('customerManagement.clientList.groupFetchError') || 'An error occurred while fetching group list.'
                //     });
                // }

                const params = buildClientGroupParams(groupName, creationTime);
                fetchClientGroupList(params, pagination.current, pagination.pageSize).then(res => {
                    setClientGroupData(res.list);
                    setTotal(res.total);
                });
                setLastUpdated(Math.floor(Date.now() / 1000));
            } catch (error: any) {
                toast.error({
                    message: error?.message || t('customerManagement.clientList.fetchError') || 'An error occurred while fetching data.'
                });
            } finally {
                setLoading(false);
            }
        };
        // if (!viewingRecord) {
            fetchData();
        // } else {
            // Fetch client history commissions when viewing details
            // const fetchHistoryData = async () => {
            //     setLoading(true);
            //     try {
            //         const res = await fetchHistory({ user_id: UID }, pagination.current, pagination.pageSize);
            //         setClientHistoryCommissions(res.list);
            //     } catch (error: any) {
            //         toast.error({
            //             message: error?.message || t('customerManagement.clientList.fetchError') || 'An error occurred while fetching data.'
            //         });
            //     } finally {
            //         setLoading(false);
            //     }
            // };
            // fetchHistoryData();
        // }s
    }, [pagination.current, pagination.pageSize]);

    // User selection logic using backend fuzzy search
    // Fetch users from backend on searchValue change (debounced)
    useEffect(() => {
        let timeout: NodeJS.Timeout;
        if (searchValue.trim() !== '') {
            timeout = setTimeout(async () => {
                try {
                    const agentIDs = await fetchAgentID(searchValue.trim(), 1);
                    setAllUsers(agentIDs.map((uid: string) => ({ uid })))
                } catch (e) {
                    setAllUsers([]);
                }
            }, 300); // debounce 300ms
        } else {
            setAllUsers([]);
        }
        return () => clearTimeout(timeout);
    }, [searchValue]);

    return (
        <div className="bg-mainLayout dark:bg-dark-mainLayout min-h-screen px-6 pt-6 pb-40 md:pb-6">
            <>
                <GenericQueryCard
                    title={t('customerManagement.clientGroup.title')}
                    subtitle={t('customerManagement.clientGroup.dataUpdatedAt').replace('{time}', lastUpdated ? new Date(lastUpdated * 1000).toLocaleString() : '2025-07-11 20:00:00')}
                    fields={queryFields}
                    onReset={handleReset}
                    onQuery={handleQuery}
                    gridCols="grid-cols-2 md:grid-cols-4"
                />
                
                {/* Table Container with Custom Layout */}
                <div className="mt-6 bg-card-background dark:bg-card-background rounded-lg p-6 overflow-hidden">
                    <div className="flex items-center justify-between mb-6">
                        <div className="text-sel-normal dark:text-dark-sel-normal text-xl font-normal font-inter leading-normal">
                            {t('customerManagement.clientGroup.table.dataList')}
                        </div>
                        <div className="flex items-center ">
                            <button 
                                className="bg-tabline dark:bg-dark-tabline text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-inter leading-tight px-4 py-1.5 rounded whitespace-nowrap flex items-center gap-2"
                                onClick={() => handleCreateClick()}
                            >
                                <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                                {t('customerManagement.clientGroup.table.addGroup')}
                            </button>
                        </div>
                    </div>
                    
                    {/* Table Content */}
                    <div className="hidden md:block h-[500px] overflow-hidden overflow-y-auto overflow-x-auto">
                        <Table
                            dataSource={clientGroupData}
                            columns={clientGroupColumns}
                            rowKey="groupId"
                            pagination={false}
                            loading={loading}
                            size="small"
                            // scroll={{ x: 1200 }}
                            className="custom-action-table"
                            showHeader={true}
                            locale={{ emptyText: '' }}
                            components={{
                                body: {
                                    wrapper: (props: any) => <tbody {...props} className="bg-card-background dark:bg-card-background" />,
                                }
                            }}
                        />
                    </div>

                    <div className="block md:hidden">
                        {clientGroupData.map((row) => (
                            <MobileTableCard
                                key={row.groupId}
                                record={row}
                                columns={mobileClientGroupColumns}
                                t={t}
                            />
                        ))}
                    </div>

                    <div className="mt-4 flex justify-end">
                        <Pagination
                            currentPage={pagination.current}
                            totalPages={Math.ceil(total / pagination.pageSize)}
                            onPageChange={(page: number) => setPagination({ ...pagination, current: page })}
                            pageSize={pagination.pageSize}
                            setPageSize={(size: number) => setPagination({ ...pagination, pageSize: size, current: 1 })}
                            pageSizes={[10, 20, 50, 100]}
                            isPageSizeVisible={true}
                            disabled={loading}
                        />
                    </div>
                </div>

                <Drawer
                    title={
                        <div className="text-sel-normal dark:text-dark-sel-normal text-xl font-normal font-inter flex justify-between">
                            <div>
                                {isCreate ? t('customerManagement.clientGroup.create.addTitle') : t('customerManagement.clientGroup.create.manageTitle')}
                            </div>
                            <svg 
                                onClick={handleDrawerClose} 
                                className="cursor-pointer text-chart-title-text hover:text-white transition-colors" 
                                width="24" 
                                height="24" 
                                viewBox="0 0 24 24" 
                                fill="none"
                            >
                                <path 
                                    d="M18 6L6 18M6 6l12 12" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                />
                            </svg>
                        </div>
                    }
                    placement="right"
                    size="large"
                    closeIcon={false}
                    onClose={handleDrawerClose}
                    open={drawerOpen}
                    zIndex={30}
                    styles={{
                        header: {
                            background: '#16182E',
                            borderBottom: '0px',
                            padding: '20px 24px'
                        },
                        body: {
                            background: '#16182E',
                            padding: '24px'
                        }
                    }}
                >
                    {(() => {
                        // Always use { group: ClientGroup, ... } shape for record
                        const record: ClientGroupDetails = selectedRecord || {
                            group: {
                                groupId: '',
                                groupName: '',
                                creationTime: '',
                                memberCount: 0,
                                depositorCount: 0,
                                totalDeposit: 0,
                                totalVolume: 0,
                                totalCommission: 0,
                                spotRatio: 0,
                                futuresRatio: 0,
                                alphaRatio: 0,
                            },
                            members: [
                                {
                                    userId: '',
                                    joinedAt: '',
                                    userDetail: {
                                        email: '',
                                        lastLoginAt: ''
                                    }
                                }
                            ],
                        };
                        return (
                            <div className="space-y-6">
                                {/* Client UID Section */}
                                <div className="space-y-[6px]">
                                    <div className="flex items-start gap-[6px]">
                                        <span className="text-place-order-bt-sell-text dark:text-dark-place-order-bt-sell-text text-sm font-normal font-inter leading-none">*</span>
                                        <span className="text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none">{t('customerManagement.clientGroup.create.groupName')}</span>
                                    </div>
                                    <input
                                        type="text"
                                        placeholder={t('customerManagement.clientGroup.create.groupNameMaxLength')}
                                        className="bg-card-background dark:bg-card-background border border-normal dark:border-dark-normal focus:ring-0 focus:outline-none rounded-lg px-3 py-[15px] w-full text-sel-normal dark:text-dark-sel-normal text-xs font-medium font-inter leading-none"
                                        maxLength={12}
                                        onChange={e => setSelectedRecord(r => r ? {
                                            ...r,
                                            group: {
                                                ...r.group,
                                                groupName: e.target.value
                                            }
                                        } : {
                                            group: {
                                                groupId: '',
                                                groupName: e.target.value,
                                                creationTime: '',
                                                memberCount: 0,
                                                depositorCount: 0,
                                                totalDeposit: 0,
                                                totalVolume: 0,
                                                totalCommission: 0,
                                                spotRatio: 0,
                                                futuresRatio: 0,
                                                alphaRatio: 0,
                                            },
                                            members: []
                                        })}
                                        value={record.group.groupName}
                                    />
                                </div>

                                {/* Commission Settings Section */}
                                <div className="flex-col">
                                    <div className="flex items-start gap-[6px] mb-[6px]">
                                        <span className="text-place-order-bt-sell-text dark:text-dark-place-order-bt-sell-text text-sm font-normal font-inter leading-none">*</span>
                                        <span className="text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none">{t('customerManagement.clientGroup.create.commissionSettings')}</span>
                                    </div>

                                    {/* Spot Trading */}
                                    <div className="space-y-3 mb-4 bg-card-background dark:bg-card-background border border-normal dark:border-dark-normal rounded-lg p-4">
                                        <span className="text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none">{t('customerManagement.clientGroup.create.spot')}</span>
                                        <div className="flex items-center gap-2">
                                            <Input
                                                placeholder={t('customerManagement.clientGroup.create.clientRatio')}
                                                suffix={<span className="ml-[2px] text-normal dark:text-dark-normal text-sm font-medium font-sans leading-tight">%</span>}
                                                className="flex flex-1 bg-card-background dark:bg-card-background border border-normal dark:border-dark-normal rounded-lg px-3 py-[15px] text-sel-normal dark:text-dark-sel-normal text-xs"
                                                onChange={e => {
                                                    let value = e.target.value.replace(/\D/g, '');
                                                    let num = Number(value);
                                                    if (num > maxSpotRatio) num = maxSpotRatio;
                                                    setSelectedRecord(r => r ? {
                                                        ...r,
                                                        group: {
                                                            ...r.group,
                                                            spotRatio: num
                                                        }
                                                    } : {
                                                        group: {
                                                            groupId: '',
                                                            groupName: '',
                                                            creationTime: '',
                                                            memberCount: 0,
                                                            depositorCount: 0,
                                                            totalDeposit: 0,
                                                            totalVolume: 0,
                                                            totalCommission: 0,
                                                            spotRatio: num,
                                                            futuresRatio: 0,
                                                            alphaRatio: 0,
                                                        },
                                                        members: []
                                                    });
                                                }}
                                                value={record.group.spotRatio ?? 0}
                                            />
                                            <div>+</div>
                                            <Input
                                                placeholder={t('customerManagement.clientGroup.create.clientRatio')}
                                                suffix={<span className="ml-[2px] text-normal dark:text-dark-normal text-sm font-medium font-sans leading-tight">%</span>}
                                                className="flex flex-1 bg-button-hover dark:bg-dark-button-hover border border-normal dark:border-dark-normal rounded-lg px-3 py-[15px] text-sel-normal dark:text-dark-sel-normal text-xs"
                                                value={maxSpotRatio - (record.group.spotRatio ?? 0)}
                                                disabled={true}
                                            />
                                            <div>=</div>
                                            <span className="text-special-text text-sm font-medium font-sans leading-tight">{maxSpotRatio}%</span>
                                        </div>
                                    </div>

                                    {/* Futures Trading */}
                                    <div className="space-y-3 mb-4 bg-card-background dark:bg-card-background border border-normal dark:border-dark-normal rounded-lg p-4">
                                        <span className="text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none">{t('customerManagement.clientGroup.create.futures')}</span>
                                        <div className="flex items-center gap-2">
                                            <Input
                                                placeholder={t('customerManagement.clientGroup.create.clientRatio')}
                                                suffix={<span className="ml-[2px] text-normal dark:text-dark-normal text-sm font-medium font-sans leading-tight">%</span>}
                                                className="flex flex-1 bg-card-background dark:bg-card-background border border-normal dark:border-dark-normal rounded-lg px-3 py-[15px] text-sel-normal dark:text-dark-sel-normal text-xs"
                                                onChange={e => {
                                                    let value = e.target.value.replace(/\D/g, '');
                                                    let num = Number(value);
                                                    if (num > maxFuturesRatio) num = maxFuturesRatio;
                                                    setSelectedRecord(r => r ? {
                                                        ...r,
                                                        group: {
                                                            ...r.group,
                                                            futuresRatio: num
                                                        }
                                                    } : {
                                                        group: {
                                                            groupId: '',
                                                            groupName: '',
                                                            creationTime: '',
                                                            memberCount: 0,
                                                            depositorCount: 0,
                                                            totalDeposit: 0,
                                                            totalVolume: 0,
                                                            totalCommission: 0,
                                                            spotRatio: 0,
                                                            futuresRatio: num,
                                                            alphaRatio: 0,
                                                        },
                                                        members: []
                                                    });
                                                }}
                                                value={record.group.futuresRatio ?? 0}
                                            />
                                            <div>+</div>
                                            <Input
                                                placeholder={t('customerManagement.clientGroup.create.myRatio')}
                                                suffix={<span className="ml-[2px] text-normal dark:text-dark-normal text-sm font-medium font-sans leading-tight">%</span>}
                                                className="flex flex-1 bg-button-hover dark:bg-dark-button-hover border border-normal dark:border-dark-normal rounded-lg px-3 py-[15px] text-sel-normal dark:text-dark-sel-normal text-xs"
                                                value={maxFuturesRatio - (record.group.futuresRatio ?? 0)}
                                                disabled={true}
                                            />
                                            <div>=</div>
                                            <span className="text-special-text text-sm font-medium font-sans leading-tight">{maxFuturesRatio}%</span>
                                        </div>
                                    </div>
                                </div>

                                {/* New User Section scrollable */}
                                <div className="flex flex-row min-h-[250px] max-h-[300px] bg-card-background dark:bg-card-background border border-normal dark:border-dark-normal rounded-lg p-4">
                                    {/* Left: Search and user list */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center border-b border-normal dark:border-dark-normal mb-2 px-2 py-1">
                                            <svg className="w-5 h-5 text-normal dark:text-dark-normal mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                <circle cx="11" cy="11" r="8" />
                                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                            </svg>
                                            <input
                                                className="bg-transparent outline-none border-none w-full text-sel-normal dark:text-dark-sel-normal text-sm"
                                                placeholder={t('customerManagement.clientGroup.create.pleaseEnterCustomerUID')}
                                                value={searchValue}
                                                onChange={e => setSearchValue(e.target.value)}
                                            />
                                        </div>
                                        <div className="max-h-48 overflow-y-auto">
                                            {filteredUsers.length === 0 ? (
                                                <div className="text-center text-normal dark:text-dark-normal text-xs py-2">{t('customerManagement.clientGroup.create.notAvailable')}</div>
                                            ) : (
                                                filteredUsers.map(user => (
                                                    <div
                                                        key={user.uid}
                                                        className="px-2 py-1 cursor-pointer hover:bg-button-hover dark:hover:bg-dark-button-hover rounded"
                                                        onClick={() => handleSelectUser(user.uid)}
                                                    >
                                                        {user.uid}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                    {/* Divider */}
                                    <div className="w-px bg-normal dark:bg-dark-normal mx-4" />
                                    {/* Right: Selected UIDs */}
                                    <div className="flex-1 min-w-0">
                                        <div className="mb-2 text-normal dark:text-dark-normal text-sm">
                                            {t('customerManagement.clientGroup.create.selected')}: {selectedUIDs.length}{t('customerManagement.clientGroup.create.people')}
                                        </div>
                                        <div className="max-h-48 overflow-y-auto">
                                            {selectedUIDs.length === 0 ? (
                                                <div className="text-center text-normal dark:text-dark-normal text-xs py-2">{t('customerManagement.clientGroup.create.noMember')}</div>
                                            ) : (
                                                selectedUIDs.map(uid => (
                                                    <div key={uid} className="px-2 py-1 flex items-center justify-between">
                                                        <span>{uid}</span>
                                                        {/* <button onClick={() => handleRemoveUser(uid)} className="text-danger dark:text-dark-danger text-xs ml-2">×</button> */}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex justify-end gap-2 pt-6 pb-6 px-6">
                                    <button
                                        onClick={handleDrawerClose}
                                        className="bg-transparent border border-dark-main dark:border-main opacity-60 rounded-lg px-6 py-2 text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-inter leading-tight cursor-pointer"
                                    >
                                        {t('customerManagement.clientGroup.create.cancel')}
                                    </button>
                                    <button
                                        onClick={() => {
                                            console.log('Save client management settings');
                                            handleDrawerSave();
                                        }}
                                        className="bg-tabline border-none rounded-lg px-6 py-2 text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-inter leading-tight cursor-pointer"
                                    >
                                        {t('customerManagement.clientGroup.create.confirm')}
                                    </button>
                                </div>
                            </div>
                        );
                    })()}
                </Drawer>
            </>
        </div>
    );
};

export default ClientGroup;